import { ResultOf } from "gql.tada";
import { SlidingThumbnailHeroById } from "@queries/entries/SlidingThumbnailHeroById";
import { AnimatedSplitLines } from "@components/shared/AnimatedSplitLines";
import { useAnimate, useMotionValue, useTransform } from "motion/react";
import { animationCurves } from "@utils/animationCurves";
import { useMountEffect } from "@react-hookz/web";
import { InfiniteAnimationFrame } from "./InfiniteAnimationFrame";
import {
  BLOCKS,
  MARKS,
  Document as RichTextDocument,
} from "@contentful/rich-text-types";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";

export const SlidingThumbnailHero = ({
  entry: { slidingThumbnailHero: entry },
}: {
  entry: ResultOf<typeof SlidingThumbnailHeroById>;
}) => {
  const entranceAnimationValue = useMotionValue(0);
  const titleValue = useTransform(entranceAnimationValue, [0, 1], [-1, 0]);
  const [, animate] = useAnimate();
  useMountEffect(async () => {
    animate(entranceAnimationValue, 1, animationCurves.long);
  });

  if (!entry) return null;

  return (
    <section className="h-screen w-full flex justify-center items-center relative">
      <div className="container-base z-10 pointer-events-none text-center text-balance flex flex-col justify-center items-center h-full">
        <h1
          className="text-display"
          style={{
            lineHeight: "calc(94% * var(--locale-scaling-line-height, 1))",
          }}
        >
          {documentToReactComponents(
            entry.richTextTitle?.json as RichTextDocument,
            {
              renderNode: {
                [BLOCKS.PARAGRAPH]: (_, children) => (
                  <span className="block">
                    <AnimatedSplitLines
                      value={titleValue}
                      animationType="clipPath"
                    >
                      {children}
                    </AnimatedSplitLines>
                  </span>
                ),
              },
              renderMark: {
                [MARKS.ITALIC]: (text) => (
                  <span className="text-palette-current-highlight">{text}</span>
                ),
              },
            }
          )}
        </h1>
        {/* subtitle field removed in schema migration */}
      </div>

      <div className="absolute inset-0 h-screen w-full overflow-hidden">
        <InfiniteAnimationFrame entry={entry} />
      </div>
    </section>
  );
};
